// Modal.js

import React, { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RowObject, Spell, SpellGroup } from "../../Types/TableTypes";
import { ApiMonsterDetail, ApiResource, MonsterDto, SpellDto } from "../../Types/ApiTypes";
import { getMonster } from "../../Utils/5eSrdApi";
import { convertSpell, getDistanceFromFt, getTranslation } from "../../Utils/Converters";
import { AllMonstersContext } from "../../Context/AllMonstersContext";
import { getSpellByIndex } from "../../Utils/LocalApi";

interface SpellSearchModalProps{
    addMonster: (monster: RowObject)=> void,
    addSpell: (monsterIndex: number, spellGroupIndex: number, spell: Spell)=> void,
    nextMonsterIndex: number,
    closeModal: ()=> void
}

const MonsterSearchModal = (props: SpellSearchModalProps) => {
    const backgroundaRef = useRef<HTMLDivElement>(null);
    const [foundMonsters, setFoundMounsters] = useState([] as MonsterDto[]);
    const { t } = useTranslation();
    const allMonsters = useContext(AllMonstersContext) as MonsterDto[];

    return (
        <>
            {<div className="modal" tabIndex={-1} style={{display: "block"}}>
                <div 
                    ref={backgroundaRef}
                    style={{display: "block", opacity: "1", backgroundColor:"#000000ad" ,width: "100%", height: "100%", position: "absolute"}} 
                    tabIndex={-1} role="dialog" aria-labelledby="myLargeModalLabel" aria-hidden="true" 
                    onClick={e=>{
                        if(e.target === backgroundaRef.current)
                        {
                            props.closeModal(); 
                        }
                    }}>
                    <div className="modal-dialog text-black" style={{maxWidth: "75%", top: "70px"}}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">{t("monster-search")}</h4>
                                <button type="button" className="btn-close" onClick={e=>props.closeModal()} aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                <input
                                    type="text"
                                    className="form-control text-center"
                                    placeholder={t("monster-search")+"..."}
                                    // value={value}
                                    onChange={(e) => {
                                        let input = e.target.value;
                                        let monsters = allMonsters.filter((monsters: MonsterDto) => getTranslation(monsters.name).toLowerCase().includes(input.toLowerCase()));
                                        setFoundMounsters([...monsters]);
                                    }}
                                ></input>

                                <div className="suggestions monsters fs-4">
                                    {foundMonsters.length >0  && foundMonsters.map((apiMonster :MonsterDto, index) => (
                                        <div className="suggestion-item" key={index}
                                            onClick={e=>{
                                                // getMonster(apiMonster.index).then(convertMonster);
                                                convertMonster(apiMonster);
                                                //props.addMonster(newMonster);//needs refactoring everything
                                                props.closeModal();
                                            }}
                                        >
                                            {getTranslation(apiMonster.name)+(apiMonster.source && apiMonster.source.year > 0 ? " ("+apiMonster.source.year+")" : "")}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>}
        </>
        
    );

    function convertSense(sense: string){
        let [distance, ...text] = sense.split("ft.");

        return {
            distance: getDistanceFromFt(sense.includes("ft.") ? parseFloat(distance) : 0),
            text: sense.includes("ft.") ? (text.length > 1 ? text.join("ft.") : text.length > 0 ? text.join("") : sense) : ""
        }
    }

    function convertMonster(monster: MonsterDto){
        // let condition_immunities = "";
        // if (monster.condition_immunities){
        //     (monster.condition_immunities.forEach((element, index) => {
        //         condition_immunities += element.name;
        //         if (index < monster.condition_immunities.length -1){
        //             condition_immunities += "; ";
        //         }
        //     }));
        // }
        let newMonster = {
            id: 0,
            encounterStats: {
                expanded: true,
                initiative: 0, 
                name: getTranslation(monster.name), 
                hp: monster.hitPoints, 
                maxHp: monster.hitPoints, 
                tempHp: 0, 
                ac: monster.armorClass ? monster.armorClass : 12, 
                conditions: "", 
                reaction: false, 
            },
            creatureStats: {
                type: getTranslation(monster.type),
                size: getTranslation(monster.size),
                alignment: getTranslation(monster.alignment),
                hasSpellCasting: false,
                initiativeBonus: /*monster.initiative??*/ Math.round((monster.statDex - 11)/2),
                isPlayer: false,
                // speed: convertSpeed(monster.speed),
                speed: {
                    walk: monster.walkDistance,
                    fly: monster.flyDistance,
                    swim: monster.swimDistance,
                    climb: monster.climbDistance,
                    burrow: monster.burrowDistance,
                    other: getTranslation(monster.otherDistance)
                },
                stats: {
                    str: monster.statStr,
                    dex: monster.statDex,
                    con: monster.statCon,
                    int: monster.statInt,
                    wis: monster.statWis,
                    cha: monster.statCha,
                    strSave: monster.saveStr,
                    dexSave: monster.saveDex,
                    conSave: monster.saveCon,
                    intSave: monster.saveInt,
                    wisSave: monster.saveWis,
                    chaSave: monster.saveCha,
                },
                otherStats: {
                    skills: getTranslation(monster.skills),
                    damageVulnerabilities: getTranslation(monster.damageVulnerabilities),
                    damageResistances: getTranslation(monster.damageResistances),
                    damageImmunities: getTranslation(monster.damageImmunities),
                    conditionImmunities: getTranslation(monster.conditionImmunities),
                    languages: getTranslation(monster.languages),
                    challenge: getTranslation(monster.challenge),
                    senses: {
                        passivePerception: monster.passivePerception,
                        darkvision: {distance: monster.darkvisionDistance, text: getTranslation(monster.darkvisionText)},
                        blindsight: {distance: monster.blindsightDistance, text: getTranslation(monster.blindsightText)},
                        tremorsense: {distance: monster.tremorsenseDistance, text: getTranslation(monster.tremorsenseText)},
                        truesight: {distance: monster.truesightDistance, text: getTranslation(monster.truesightText)},
                    }
                },
                proficiencyBonus: monster.proficiencyBonus,
                equipmentText: getTranslation(monster.equipmentText),
                equipment: [] as {title: string, description: string}[],
                reactionsText: getTranslation(monster.reactionsText),
                reactions: [] as {title: string, description: string}[],
                traitsText: getTranslation(monster.traitsText),
                traits: [] as {title: string, description: string}[],
                actionsText: getTranslation(monster.actionsText),
                actions: [] as {title: string, description: string}[],
                bonusActionsText: getTranslation(monster.bonusActionsText),
                bonusActions: [] as {title: string, description: string}[],
                legendaryActionsText: getTranslation(monster.legendaryActionsText),
                legendaryActions: [] as {title: string, description: string}[],
                spellcasting: {
                    spellCharacteristic: getTranslation(monster.spellCharacteristic),
                    spellSave: monster.spellSave ?? 0,
                    spellAttackMod: monster.spellAttackMod ?? 0,
                    spellGroups: [] as SpellGroup[]
                }
            }
        };

        // monster.special_abilities.forEach(element => {
        //     if (element.spellcasting){
        //        newMonster.creatureStats.spellcasting = convert5eSrdSpellcasting(element.spellcasting);
        //     }
        //     else{
        //         newMonster.creatureStats.traits.push({title: element.name, description: element.desc});
        //     }
        // });
        monster.traits.forEach(element => {
            newMonster.creatureStats.traits.push({title: getTranslation(element.title), description: getTranslation(element.description)});
        });

        monster.actions.forEach(element => {
            newMonster.creatureStats.actions.push({title: getTranslation(element.title), description: getTranslation(element.description)});
        });

        monster.bonusActions.forEach(element => {
            newMonster.creatureStats.bonusActions.push({title: getTranslation(element.title), description: getTranslation(element.description)});
        });

        monster.reactions.forEach(element => {
            newMonster.creatureStats.reactions.push({title: getTranslation(element.title), description: getTranslation(element.description)});
        });

        monster.legendaryActions.forEach(element => {
            newMonster.creatureStats.legendaryActions.push({title: getTranslation(element.title), description: getTranslation(element.description)});
        });        

        monster.spellGroups && monster.spellGroups.forEach(group => {
            let newGroup: SpellGroup = {
                label: getTranslation(group.name),
                hasSlotsGroup: group.hasSlotsGroup,
                hasSlotsEach: group.hasSlotsEach,
                maxSlots: group.maxSlots,
                remainingSlots: group.maxSlots,
                list: [] as Spell[]
            }
            group.spells.forEach(spell => {
                newGroup.list.push(convertSpell(spell));
            })

            newMonster.creatureStats.spellcasting.spellGroups.push(newGroup);
        });
        // newMonster.creatureStats.otherStats.skills = proficiencies;
        props.addMonster(newMonster);
    }  
    
    function convertSpeed(speed: {
        walk: string,
        fly: string,
        swim: string,
        climb: string,
        burrow: string
    }) {
        let walkSpeedFt = speed.walk && speed.walk.includes("ft.") ? parseFloat(speed.walk.split(" ")[0]) : 0;
        let flySpeedFt = speed.fly && speed.fly.includes("ft.") ? parseFloat(speed.fly.split(" ")[0]) : 0;
        let swimSpeedFt = speed.swim && speed.swim.includes("ft.") ? parseFloat(speed.swim.split(" ")[0]) : 0;
        let climbSpeedFt = speed.climb && speed.climb.includes("ft.") ? parseFloat(speed.climb.split(" ")[0]) : 0;
        let burrowSpeedFt = speed.burrow && speed.burrow.includes("ft.") ? parseFloat(speed.burrow.split(" ")[0]) : 0;
        return {
            walk: getDistanceFromFt(walkSpeedFt),
            fly: getDistanceFromFt(flySpeedFt),
            swim: getDistanceFromFt(swimSpeedFt),
            climb: getDistanceFromFt(climbSpeedFt),
            burrow: getDistanceFromFt(burrowSpeedFt),
            other: ""
        }
    };

    function convert5eSrdSpellcasting(spellcasting: { level: number; ability: ApiResource; dc: number; modifier: number; components_required: string[]; school: string; slots: { 1: number; 2: number; 3: number; 4: number; 5: number; 6: number; 7: number; 8: number; 9: number; }; spells: {name: string,level: number,url: string,usage: {type: string,times: number,rest_types: string[]}}[]; id: string; }): { spellCharacteristic: string; spellSave: number; spellAttackMod: number; spellGroups: SpellGroup[]; } {
        let newSpellcasting = {
            spellCharacteristic: spellcasting.ability ? spellcasting.ability.name : "",
            spellSave: spellcasting.dc ?? 0,
            spellAttackMod: spellcasting.modifier ?? 0,
            spellGroups: [] as SpellGroup[]
        };

        if (spellcasting.slots){
            newSpellcasting.spellGroups.push({
                label: "Cantrips",
                hasSlotsGroup:false,
                hasSlotsEach: false,
                remainingSlots: 0,
                maxSlots: 0,
                list: [] as Spell[]
            });

            //create groups
            for (let i=1 as keyof {1: number;2: number;3: number;4: number;5: number;6: number;7: number;8: number;9: number;}; i<= 9; i++){
                if (spellcasting.slots[i]){
                    newSpellcasting.spellGroups.push({
                        label: "Level "+i,
                        hasSlotsGroup:true,
                        hasSlotsEach: false,
                        remainingSlots: spellcasting.slots[i],
                        maxSlots: spellcasting.slots[i],
                        list: [] as Spell[]
                    });
                }
            }
            spellcasting.spells.forEach(element =>{
                let splitUrl = element.url.split("/");
                getSpellByIndex(splitUrl[splitUrl.length -1])
                    .then((spell: SpellDto) => 
                        props.addSpell(props.nextMonsterIndex, element.level,convertSpell(spell)));
            });
        }
        else{
            //create groups
            spellcasting.spells.forEach(element =>{
                let foundGroup = false;
                    for (let i=0; i< newSpellcasting.spellGroups.length; i++){
                        if (newSpellcasting.spellGroups[i].label === (element.usage.times ? element.usage.times+" "+element.usage.type : element.usage.type)){
                            foundGroup = true;
                            break;
                        }
                    }
                    if (!foundGroup){
                        newSpellcasting.spellGroups.push({
                            label: element.usage.times ? element.usage.times+" "+element.usage.type : element.usage.type,
                            hasSlotsGroup:false,
                            hasSlotsEach: element.usage.times ? true : false,
                            remainingSlots: element.usage.times ?? 0,
                            maxSlots: element.usage.times ?? 0,
                            list: [] as Spell[]
                        })
                    }          

            });

            //add spells
            spellcasting.spells.forEach(element =>{
                let splitUrl = element.url.split("/");
                getSpellByIndex(splitUrl[splitUrl.length -1]).then((spell: SpellDto)=>{    
                    let newSpell = convertSpell(spell);
                    newSpell.remainingSlots = element.usage.times;
                    for (let i=0; i< newSpellcasting.spellGroups.length; i++){
                        if (newSpellcasting.spellGroups[i].label === (element.usage.times ? element.usage.times+" "+element.usage.type : element.usage.type)){
                            newSpellcasting.spellGroups[i].list.push(newSpell);
                            getSpellByIndex(splitUrl[splitUrl.length -1]).then((spell: SpellDto) => props.addSpell(props.nextMonsterIndex, i, convertSpell(spell)));
                            break;
                        }
                    }
                });
            });
        }
        return newSpellcasting;
    } 
};

export default MonsterSearchModal;
